import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  executeSaLegacyMigration,
  getSaLegacyClients,
  getSaLegacyConnection,
  getSaLegacyRun,
  previewSaLegacyMigration,
  SaLegacyClient,
  SaLegacyConnection,
  SaLegacyPreviewClient,
  SaLegacyRun,
} from "../../../helpers/super_admin_legacy_helper";
import {
  getSuperAdminPlans,
  SaPlan,
} from "../../../helpers/super_admin_plans_helper";

type ClientDraft = {
  selected: boolean;
  plan_id: number | "";
  billing_channel: "self_serve" | "brdominio";
};

function saErrMessage(e: unknown, fallback: string): string {
  const ax = e as { response?: { data?: { error?: { message?: string } } } };
  return ax?.response?.data?.error?.message || fallback;
}

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Cutover legado → F1 (LAC-010).
 * Por quê tela SA: Roberto escolhe clientes + plano sem CLI; o Job na fila `import` faz o peso.
 */
const SuperAdminLegacyMigration = () => {
  const { t } = useTranslation();

  const [connection, setConnection] = useState<SaLegacyConnection | null>(null);
  const [clients, setClients] = useState<SaLegacyClient[]>([]);
  const [plans, setPlans] = useState<SaPlan[]>([]);
  const [drafts, setDrafts] = useState<Record<number, ClientDraft>>({});
  const [search, setSearch] = useState("");
  const [onlyPending, setOnlyPending] = useState(true);
  const [includeCampaigns, setIncludeCampaigns] = useState(true);
  const [includeFields, setIncludeFields] = useState(true);
  const [includeFiles, setIncludeFiles] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SaLegacyPreviewClient[] | null>(null);
  const [previewForIds, setPreviewForIds] = useState<number[]>([]);
  const [run, setRun] = useState<SaLegacyRun | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [connRes, clientsRes, plansRes] = await Promise.all([
        getSaLegacyConnection(),
        getSaLegacyClients(search ? { search } : undefined),
        getSuperAdminPlans({ per_page: 100 }),
      ]);
      setConnection(connRes.data);
      setClients(clientsRes.data.items);
      const planList = Array.isArray(plansRes.data) ? plansRes.data : [];
      setPlans(planList.filter((p) => p.is_active));

      setDrafts((prev) => {
        const next = { ...prev };
        for (const c of clientsRes.data.items) {
          if (!next[c.legacy_cliente_id]) {
            next[c.legacy_cliente_id] = {
              selected: false,
              plan_id: "",
              billing_channel: "brdominio",
            };
          }
        }
        return next;
      });
    } catch (e) {
      setError(saErrMessage(e, t("sa.legacy.error.load")));
    } finally {
      setLoading(false);
    }
  }, [search, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll do run enquanto running/queued.
  useEffect(() => {
    if (!run || !["running", "queued"].includes(run.status)) {
      return;
    }
    const timer = window.setInterval(async () => {
      try {
        const res = await getSaLegacyRun(run.id);
        setRun(res.data);
        if (["completed", "failed"].includes(res.data.status)) {
          void load();
        }
      } catch {
        /* ignore transient */
      }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [run, load]);

  const visibleClients = useMemo(() => {
    if (!onlyPending) return clients;
    return clients.filter((c) => c.migration_status !== "completed");
  }, [clients, onlyPending]);

  const selectedIds = useMemo(
    () =>
      Object.entries(drafts)
        .filter(([, d]) => d.selected)
        .map(([id]) => Number(id))
        .filter((id) => visibleClients.some((c) => c.legacy_cliente_id === id)),
    [drafts, visibleClients]
  );

  const previewMatchesSelection = useMemo(() => {
    if (!preview || previewForIds.length === 0) return false;
    if (previewForIds.length !== selectedIds.length) return false;
    const a = [...previewForIds].sort((x, y) => x - y);
    const b = [...selectedIds].sort((x, y) => x - y);
    return a.every((id, i) => id === b[i]);
  }, [preview, previewForIds, selectedIds]);

  const previewTotals = useMemo(() => {
    if (!preview) return null;
    return preview.reduce(
      (acc, p) => {
        acc.emails += p.counts?.emails_active ?? 0;
        acc.tags += p.counts?.tags ?? 0;
        acc.campaigns += p.counts?.campaigns ?? 0;
        acc.fields += p.counts?.field_definitions ?? 0;
        acc.filesOk += p.counts?.files_resolvable ?? 0;
        acc.filesMissing += p.counts?.files_missing ?? 0;
        return acc;
      },
      { emails: 0, tags: 0, campaigns: 0, fields: 0, filesOk: 0, filesMissing: 0 }
    );
  }, [preview]);

  const runProgress = useMemo(() => {
    const maps = run?.maps ?? [];
    if (maps.length === 0) {
      return { total: 0, done: 0, pct: 0, label: "" };
    }
    const done = maps.filter((m) =>
      ["completed", "failed", "skipped"].includes(m.status)
    ).length;
    const pct = Math.round((done / maps.length) * 100);
    return {
      total: maps.length,
      done,
      pct,
      label: t("sa.legacy.progressLabel", { done, total: maps.length }),
    };
  }, [run, t]);

  const showFilesRootWarning =
    includeFiles && connection !== null && !connection.files_root_configured;

  const toggleAll = (checked: boolean) => {
    setDrafts((prev) => {
      const next = { ...prev };
      for (const c of visibleClients) {
        if (c.migration_status === "completed") continue;
        next[c.legacy_cliente_id] = {
          ...(next[c.legacy_cliente_id] ?? {
            plan_id: "",
            billing_channel: "brdominio" as const,
          }),
          selected: checked,
          plan_id: next[c.legacy_cliente_id]?.plan_id ?? "",
          billing_channel:
            next[c.legacy_cliente_id]?.billing_channel ?? "brdominio",
        };
      }
      return next;
    });
  };

  const onPreview = async () => {
    if (selectedIds.length === 0) {
      toast.warn(t("sa.legacy.needSelection"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await previewSaLegacyMigration({
        legacy_cliente_ids: selectedIds,
        include_campaigns: includeCampaigns,
        include_fields: includeFields,
        include_files: includeFiles,
      });
      setPreview(res.data.clients);
      setPreviewForIds([...selectedIds]);
      toast.success(t("sa.legacy.previewOk"));
    } catch (e) {
      setError(saErrMessage(e, t("sa.legacy.error.preview")));
    } finally {
      setBusy(false);
    }
  };

  const onExecute = async () => {
    if (selectedIds.length === 0) {
      toast.warn(t("sa.legacy.needSelection"));
      return;
    }
    if (!previewMatchesSelection || !previewTotals) {
      toast.warn(t("sa.legacy.needPreviewFirst"));
      return;
    }
    const payloadClients = [];
    for (const id of selectedIds) {
      const d = drafts[id];
      if (!d?.plan_id) {
        toast.warn(t("sa.legacy.needPlan", { id }));
        return;
      }
      payloadClients.push({
        legacy_cliente_id: id,
        plan_id: Number(d.plan_id),
        billing_channel: d.billing_channel,
      });
    }

    const confirmMsg = t("sa.legacy.confirmExecuteDetailed", {
      count: payloadClients.length,
      emails: previewTotals.emails,
      tags: previewTotals.tags,
      campaigns: previewTotals.campaigns,
      fields: previewTotals.fields,
      filesOk: previewTotals.filesOk,
      filesMissing: previewTotals.filesMissing,
    });
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const res = await executeSaLegacyMigration({
        clients: payloadClients,
        include_campaigns: includeCampaigns,
        include_fields: includeFields,
        include_files: includeFiles,
      });
      const runRes = await getSaLegacyRun(res.data.run_id);
      setRun(runRes.data);
      toast.success(t("sa.legacy.executeOk"));
      await load();
    } catch (e) {
      setError(saErrMessage(e, t("sa.legacy.error.execute")));
    } finally {
      setBusy(false);
    }
  };

  const downloadReportCsv = () => {
    if (!run) return;
    const rows: string[][] = [
      [
        "legacy_cliente_id",
        "tenant_id",
        "status",
        "owner_email",
        "owner_temp_password",
        "contacts_imported",
        "tags",
        "campaigns",
        "campaigns_legacy_flagged",
        "field_values",
        "files_copied",
        "files_missing",
        "error",
      ],
    ];

    const byLegacy = new Map(
      (run.report?.clients ?? []).map((c) => [c.legacy_cliente_id, c])
    );

    for (const m of run.maps ?? []) {
      const rep = byLegacy.get(m.legacy_cliente_id);
      const counts = rep?.counts ?? m.counts ?? {};
      rows.push([
        String(m.legacy_cliente_id),
        String(rep?.tenant_id ?? m.tenant_id ?? ""),
        String(rep?.status ?? m.status ?? ""),
        String(rep?.owner_email ?? ""),
        String(rep?.owner_temp_password ?? ""),
        String(counts.contacts_imported ?? ""),
        String(counts.tags ?? ""),
        String(counts.campaigns ?? ""),
        String(counts.campaigns_legacy_flagged ?? ""),
        String(counts.field_values ?? ""),
        String(counts.files_copied ?? ""),
        String(counts.files_missing ?? ""),
        String(rep?.error ?? m.error_message ?? ""),
      ]);
    }

    const bom = "\uFEFF";
    const body = rows.map((r) => r.map(csvEscape).join(",")).join("\r\n");
    const blob = new Blob([bom + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `legacy-migration-run-${run.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("sa.legacy.csvDownloaded"));
  };

  document.title = t("sa.legacy.documentTitle");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.legacy.title")}
          pageTitle={t("sa.menu.ops")}
        />
        <ToastContainer />

        {error && (
          <Alert color="danger" className="mb-3">
            {error}
            <Button color="link" className="p-0 ms-2" onClick={() => void load()}>
              {t("sa.legacy.retry")}
            </Button>
          </Alert>
        )}

        <Row className="mb-3">
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">{t("sa.legacy.connectionTitle")}</h5>
              </CardHeader>
              <CardBody>
                {loading && !connection ? (
                  <Spinner size="sm" />
                ) : connection ? (
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Badge color={connection.ok ? "success" : "danger"}>
                      {connection.ok
                        ? t("sa.legacy.connected")
                        : t("sa.legacy.disconnected")}
                    </Badge>
                    <Badge
                      color={
                        connection.files_root_configured ? "success" : "secondary"
                      }
                    >
                      {connection.files_root_configured
                        ? t("sa.legacy.filesRootOk")
                        : t("sa.legacy.filesRootMissing")}
                    </Badge>
                    <span className="text-muted small">
                      {connection.host}/{connection.database}
                      {connection.cliente_count != null
                        ? ` · ${t("sa.legacy.clientCount", {
                            count: connection.cliente_count,
                          })}`
                        : ""}
                    </span>
                    <span className="text-muted small w-100">
                      {connection.message}
                    </span>
                  </div>
                ) : null}
                <p className="text-muted small mb-0 mt-2">{t("sa.legacy.hint")}</p>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {showFilesRootWarning && (
          <Alert color="warning" className="mb-3">
            {t("sa.legacy.filesRootWarning")}
          </Alert>
        )}

        <Row className="mb-3">
          <Col md={4}>
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("sa.legacy.searchPlaceholder")}
              disabled={!connection?.ok}
            />
          </Col>
          <Col
            md={8}
            className="d-flex flex-wrap gap-3 align-items-center mt-2 mt-md-0"
          >
            <FormGroup check className="mb-0">
              <Input
                id="opt-pending"
                type="checkbox"
                checked={onlyPending}
                onChange={(e) => setOnlyPending(e.target.checked)}
              />
              <Label check for="opt-pending">
                {t("sa.legacy.filter.onlyPending")}
              </Label>
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input
                id="opt-campaigns"
                type="checkbox"
                checked={includeCampaigns}
                onChange={(e) => setIncludeCampaigns(e.target.checked)}
              />
              <Label check for="opt-campaigns">
                {t("sa.legacy.opt.campaigns")}
              </Label>
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input
                id="opt-fields"
                type="checkbox"
                checked={includeFields}
                onChange={(e) => setIncludeFields(e.target.checked)}
              />
              <Label check for="opt-fields">
                {t("sa.legacy.opt.fields")}
              </Label>
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input
                id="opt-files"
                type="checkbox"
                checked={includeFiles}
                onChange={(e) => setIncludeFiles(e.target.checked)}
              />
              <Label check for="opt-files">
                {t("sa.legacy.opt.files")}
              </Label>
            </FormGroup>
          </Col>
        </Row>

        <Card>
          <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="card-title mb-0">{t("sa.legacy.clientsTitle")}</h5>
            <div className="d-flex gap-2">
              <Button
                color="light"
                size="sm"
                disabled={busy || !connection?.ok || selectedIds.length === 0}
                onClick={() => void onPreview()}
              >
                {busy ? <Spinner size="sm" /> : t("sa.legacy.dryRun")}
              </Button>
              <Button
                color="primary"
                size="sm"
                disabled={
                  busy ||
                  !connection?.ok ||
                  selectedIds.length === 0 ||
                  !previewMatchesSelection
                }
                onClick={() => void onExecute()}
                title={
                  !previewMatchesSelection
                    ? t("sa.legacy.needPreviewFirst")
                    : undefined
                }
              >
                {t("sa.legacy.execute")}
              </Button>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {loading ? (
              <div className="p-4 text-center">
                <Spinner />
              </div>
            ) : !connection?.ok ? (
              <Alert color="warning" className="m-3">
                {t("sa.legacy.needConnection")}
              </Alert>
            ) : visibleClients.length === 0 ? (
              <p className="text-muted p-3 mb-0">
                {onlyPending
                  ? t("sa.legacy.emptyPending")
                  : t("sa.legacy.empty")}
              </p>
            ) : (
              <div className="table-responsive">
                <Table className="table-nowrap align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 40 }}>
                        <Input
                          type="checkbox"
                          onChange={(e) => toggleAll(e.target.checked)}
                          aria-label={t("sa.legacy.selectAll")}
                        />
                      </th>
                      <th>ID</th>
                      <th>{t("sa.legacy.col.name")}</th>
                      <th>{t("sa.legacy.col.email")}</th>
                      <th>{t("sa.legacy.col.active")}</th>
                      <th>{t("sa.legacy.col.status")}</th>
                      <th>{t("sa.legacy.col.plan")}</th>
                      <th>{t("sa.legacy.col.channel")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleClients.map((c) => {
                      const d = drafts[c.legacy_cliente_id];
                      const done = c.migration_status === "completed";
                      return (
                        <tr key={c.legacy_cliente_id}>
                          <td>
                            <Input
                              type="checkbox"
                              checked={!!d?.selected}
                              disabled={done}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [c.legacy_cliente_id]: {
                                    ...(prev[c.legacy_cliente_id] ?? {
                                      plan_id: "",
                                      billing_channel: "brdominio" as const,
                                    }),
                                    selected: e.target.checked,
                                    plan_id:
                                      prev[c.legacy_cliente_id]?.plan_id ?? "",
                                    billing_channel:
                                      prev[c.legacy_cliente_id]
                                        ?.billing_channel ?? "brdominio",
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>{c.legacy_cliente_id}</td>
                          <td>
                            <div>{c.name}</div>
                            {!c.active && (
                              <Badge color="secondary" className="mt-1">
                                {t("sa.legacy.inactive")}
                              </Badge>
                            )}
                          </td>
                          <td className="small">{c.email}</td>
                          <td>
                            {c.active ? (
                              <Badge color="success">{t("sa.legacy.yes")}</Badge>
                            ) : (
                              <Badge color="secondary">{t("sa.legacy.no")}</Badge>
                            )}
                          </td>
                          <td>
                            {done ? (
                              <Link to={`/super-admin/tenants/${c.tenant_id}`}>
                                {t("sa.legacy.migrated", { id: c.tenant_id })}
                              </Link>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td style={{ minWidth: 160 }}>
                            <Input
                              type="select"
                              bsSize="sm"
                              disabled={done || !d?.selected}
                              value={d?.plan_id ?? ""}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [c.legacy_cliente_id]: {
                                    ...prev[c.legacy_cliente_id],
                                    selected:
                                      prev[c.legacy_cliente_id]?.selected ??
                                      false,
                                    billing_channel:
                                      prev[c.legacy_cliente_id]
                                        ?.billing_channel ?? "brdominio",
                                    plan_id: e.target.value
                                      ? Number(e.target.value)
                                      : "",
                                  },
                                }))
                              }
                            >
                              <option value="">
                                {t("sa.legacy.choosePlan")}
                              </option>
                              {plans.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name} (#{p.id})
                                </option>
                              ))}
                            </Input>
                          </td>
                          <td style={{ minWidth: 140 }}>
                            <Input
                              type="select"
                              bsSize="sm"
                              disabled={done || !d?.selected}
                              value={d?.billing_channel ?? "brdominio"}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [c.legacy_cliente_id]: {
                                    ...prev[c.legacy_cliente_id],
                                    selected:
                                      prev[c.legacy_cliente_id]?.selected ??
                                      false,
                                    plan_id:
                                      prev[c.legacy_cliente_id]?.plan_id ?? "",
                                    billing_channel: e.target.value as
                                      | "self_serve"
                                      | "brdominio",
                                  },
                                }))
                              }
                            >
                              <option value="brdominio">
                                {t("sa.tenants.channel.brdominio")}
                              </option>
                              <option value="self_serve">
                                {t("sa.tenants.channel.selfServe")}
                              </option>
                            </Input>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        {preview && previewTotals && (
          <Card className="mt-3">
            <CardHeader>
              <h5 className="card-title mb-0">{t("sa.legacy.previewTitle")}</h5>
            </CardHeader>
            <CardBody>
              {!previewMatchesSelection && (
                <Alert color="info" className="mb-3">
                  {t("sa.legacy.previewStale")}
                </Alert>
              )}
              <p className="small mb-3">
                {t("sa.legacy.previewTotals", {
                  emails: previewTotals.emails,
                  tags: previewTotals.tags,
                  campaigns: previewTotals.campaigns,
                  fields: previewTotals.fields,
                  filesOk: previewTotals.filesOk,
                  filesMissing: previewTotals.filesMissing,
                })}
              </p>
              <div className="table-responsive">
                <Table className="mb-0 table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>{t("sa.legacy.col.name")}</th>
                      <th>{t("sa.legacy.preview.emails")}</th>
                      <th>{t("sa.legacy.preview.tags")}</th>
                      <th>{t("sa.legacy.preview.campaigns")}</th>
                      <th>{t("sa.legacy.preview.fields")}</th>
                      <th>{t("sa.legacy.preview.files")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((p) => (
                      <tr key={p.legacy_cliente_id}>
                        <td>{p.legacy_cliente_id}</td>
                        <td>{p.name ?? p.error ?? "—"}</td>
                        <td>
                          {p.counts
                            ? `${p.counts.emails_active}/${p.counts.emails_total}`
                            : "—"}
                        </td>
                        <td>{p.counts?.tags ?? "—"}</td>
                        <td>{p.counts?.campaigns ?? "—"}</td>
                        <td>{p.counts?.field_definitions ?? "—"}</td>
                        <td>
                          {p.counts
                            ? `${p.counts.files_resolvable}/${p.counts.files_missing}`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        {run && (
          <Card className="mt-3">
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <h5 className="card-title mb-0">
                {t("sa.legacy.runTitle", { id: run.id })}
              </h5>
              <div className="d-flex align-items-center gap-2">
                <Badge
                  color={
                    run.status === "completed"
                      ? "success"
                      : run.status === "failed"
                        ? "danger"
                        : "info"
                  }
                >
                  {run.status}
                </Badge>
                <Button
                  color="light"
                  size="sm"
                  onClick={downloadReportCsv}
                  disabled={(run.maps?.length ?? 0) === 0}
                >
                  {t("sa.legacy.exportCsv")}
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {runProgress.total > 0 && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between small mb-1">
                    <span>{runProgress.label}</span>
                    <span>{runProgress.pct}%</span>
                  </div>
                  <Progress
                    value={runProgress.pct}
                    color={
                      run.status === "failed"
                        ? "danger"
                        : run.status === "completed"
                          ? "success"
                          : "info"
                    }
                    animated={["running", "queued"].includes(run.status)}
                  />
                </div>
              )}
              <p className="text-muted small">{t("sa.legacy.passwordHint")}</p>
              {(run.report?.clients ?? []).map((c, idx) => (
                <Alert
                  key={`${c.legacy_cliente_id}-${idx}`}
                  color={c.status === "failed" ? "danger" : "secondary"}
                  className="mb-2"
                >
                  <div>
                    <strong>#{c.legacy_cliente_id}</strong> → tenant{" "}
                    {c.tenant_id ? (
                      <Link to={`/super-admin/tenants/${c.tenant_id}`}>
                        #{c.tenant_id}
                      </Link>
                    ) : (
                      "—"
                    )}{" "}
                    ({c.status})
                  </div>
                  {c.owner_email && c.owner_temp_password && (
                    <div className="small mt-1">
                      {c.owner_email} / <code>{c.owner_temp_password}</code>
                    </div>
                  )}
                  {c.error && <div className="small mt-1">{c.error}</div>}
                </Alert>
              ))}
              {run.maps?.map((m) => (
                <div key={m.id} className="small text-muted mb-1">
                  map #{m.legacy_cliente_id}: {m.status}
                  {m.tenant_id ? (
                    <>
                      {" "}
                      →{" "}
                      <Link to={`/super-admin/tenants/${m.tenant_id}`}>
                        tenant #{m.tenant_id}
                      </Link>
                    </>
                  ) : null}
                  {m.counts?.phase ? ` · fase ${m.counts.phase}` : ""}
                  {typeof m.counts?.contacts_imported === "number"
                    ? ` · ${m.counts.contacts_imported} contatos novos`
                    : ""}
                  {typeof m.counts?.contacts_skipped === "number"
                    ? ` · ${m.counts.contacts_skipped} já existentes`
                    : ""}
                  {typeof m.counts?.campaigns_legacy_flagged === "number"
                    ? ` · ${m.counts.campaigns_legacy_flagged} campanhas marcadas legado`
                    : typeof m.counts?.campaigns === "number"
                      ? ` · ${m.counts.campaigns} campanhas`
                      : ""}
                  {m.error_message ? (
                    <div className="text-danger mt-1">{m.error_message}</div>
                  ) : null}
                </div>
              ))}
              {run.status === "failed" && (
                <Alert color="warning" className="mt-2 mb-0">
                  {t("sa.legacy.resumeHint")}
                </Alert>
              )}
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default SuperAdminLegacyMigration;
